// Saves the editor's work as a draft shortly after each change. The live site
// is untouched until the owner publishes.
import { useCallback, useEffect, useRef, useState } from "react";
import { useSaveDraftMutation } from "../../store/Reducer/templateApis";
import { toBackendTemplateId } from "../../utils/templateIdMap";

const SAVE_DELAY = 1500;

// status: "idle" (nothing changed since opening), "pending", "saving",
// "saved", "error".
// onSaved gets the server's reply (the AI editor uses the checked copy it returns).
export const useDraftAutosave = ({ templateId, site, ready, onSaved }) => {
  const [saveDraft] = useSaveDraftMutation();
  const [status, setStatus] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);
  const lastSaved = useRef(null); // JSON of the last version the server has
  const latest = useRef({ site, templateId });
  const timer = useRef(null);
  const inFlight = useRef(null);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  latest.current = { site, templateId };

  // What the server already has (on open, after publish or discard). Any save
  // still scheduled for the old content is dropped.
  const markBaseline = useCallback((settings) => {
    clearTimeout(timer.current);
    timer.current = null;
    lastSaved.current = JSON.stringify(settings);
    setStatus("idle");
  }, []);

  // Before discarding: drop the scheduled save and let one in flight finish,
  // so nothing re-creates the draft afterwards.
  const cancelPending = useCallback(async () => {
    clearTimeout(timer.current);
    timer.current = null;
    if (inFlight.current) await inFlight.current.catch(() => {});
    lastSaved.current = JSON.stringify(latest.current.site);
  }, []);

  const save = useCallback(async () => {
    clearTimeout(timer.current);
    timer.current = null;
    if (inFlight.current) await inFlight.current.catch(() => {});
    const { site: current, templateId: key } = latest.current;
    const json = JSON.stringify(current);
    if (!current || json === lastSaved.current) return true;

    setStatus("saving");
    inFlight.current = saveDraft({ templateMap: current, templateId: toBackendTemplateId(key) }).unwrap();
    try {
      const res = await inFlight.current;
      lastSaved.current = json;
      onSavedRef.current?.(res, current);
      setSavedAt(res?.data?.draftUpdatedAt ? new Date(res.data.draftUpdatedAt) : new Date());
      // Something may have changed while the request was out.
      setStatus(JSON.stringify(latest.current.site) === json ? "saved" : "pending");
      return true;
    } catch (err) {
      setStatus("error");
      return false;
    } finally {
      inFlight.current = null;
    }
  }, [saveDraft]);

  useEffect(() => {
    if (!ready || !site || lastSaved.current === null) return;
    if (JSON.stringify(site) === lastSaved.current) return;
    setStatus("pending");
    clearTimeout(timer.current);
    timer.current = setTimeout(save, SAVE_DELAY);
  }, [site, ready, save]);

  // Re-arm the timer if a change landed while a save was in flight.
  useEffect(() => {
    if (status === "pending" && !timer.current) timer.current = setTimeout(save, SAVE_DELAY);
  }, [status, save]);

  // Warn before closing the tab with unsaved work; save when leaving the editor.
  useEffect(() => {
    const warn = (e) => {
      if (JSON.stringify(latest.current.site) !== lastSaved.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
      if (lastSaved.current !== null) save();
    };
  }, [save]);

  return { status, savedAt, flush: save, markBaseline, cancelPending };
};
